/**
 * Form validation class that handles form error display and cleanup
 */
class FormValidator {
  /**
   * Initialize the FormValidator with default options
   */
  constructor() {
    this.defaultOptions = {
      errorTextClasses: 'text-red-500',
      errorTextClassesCheckRadio: 'text-red-500 absolute bottom-0 left-0',
      errorTextClassesFile: 'text-red-500',
      errorTextClassesSelect: 'text-red-500',
      inputErrorClasses: '!border-red-500',
      selectErrorClasses: '!border-red-500',
      errorTextClassesForm: 'text-red-500',
      defaultErrorMessage: 'This field is required',
      errorClearDelay: 500,
      transitionDelay: 300,
    };
  }

  /**
   * Main validation method that processes form data and displays errors
   * @param {string|object} formData - Form data as string or object
   * @returns {object} Object containing field errors
   */
  validate(formData) {
    if (!document.querySelector('.portal')) {
      try {
        const data =
          typeof formData === 'string' ? JSON.parse(formData) : formData;

        const formId = data.form_id;
        const errorMessages = data.names;
        const isFormLevelError =
          Array.isArray(errorMessages) &&
          Array.isArray(errorMessages[0]) &&
          typeof errorMessages[0][0] === 'string';
        const formErrorMessage = isFormLevelError ? errorMessages[0][0] : '';

        const finalOptions = { ...this.defaultOptions };
        const errors = {};

        const form = document.getElementById(formId);
        if (!form) {
          throw new Error(`Form with ID "${formId}" not found.`);
        }

        if (formErrorMessage && formErrorMessage !== '') {
          this.handleFormError(form, formErrorMessage, finalOptions);
        }

        if (
          errorMessages &&
          (typeof errorMessages !== 'object' ||
            (Array.isArray(errorMessages) && !isFormLevelError))
        ) {
            throw new Error('Invalid error format. Expected an object.');
        }

        if (errorMessages && !isFormLevelError) {
          Object.keys(errorMessages).forEach((fieldName) => {
            this.handleFieldError(
              form,
              fieldName,
              errorMessages[fieldName],
              finalOptions,
              errors
            );
          });
        }

        this.updateAllSimpleBars();

        return errors;
      } catch (error) {
        console.error('Error parsing JSON data:', error.message);
        return {};
      }
    }
  }

  /**
   * Handle form-level error display
   * @param {HTMLElement} form - The form element
   * @param {string} errorMessage - Error message to display
   * @param {object} options - Validation options
   */
  handleFormError(form, errorMessage, options) {
    let formErrorElement = form.querySelector('.form-error-text');
    if (formErrorElement) {
      formErrorElement.remove();
    }

    formErrorElement = document.createElement('div');
    formErrorElement.className = `form-error-text ${options.errorTextClassesForm}`;
    formErrorElement.textContent = errorMessage;

    form.classList.add('error');
    const errorAnchor = form.querySelector('[data-form-error-anchor]');

    if (errorAnchor) {
      errorAnchor.before(formErrorElement);
    } else {
      form.appendChild(formErrorElement);
    }

    if (form.querySelector('input, textarea')) {
      const inputs = form.querySelectorAll('input, textarea');
      inputs.forEach((input) => {
        input.addEventListener('click', () => {
          form.classList.remove('error');
        });

        input.addEventListener('change', () => {
          form.classList.remove('error');
        });
      });
    }
  }

  /**
   * Handle field-level error display and setup
   * @param {HTMLElement} form - The form element
   * @param {string} fieldName - Name of the field with error
   * @param {array} fieldError - Error data for the field
   * @param {object} options - Validation options
   * @param {object} errors - Errors object to populate
   */
  handleFieldError(form, fieldName, fieldError, options, errors) {
    let inputField = form.elements[fieldName];

    if (!inputField) {
      inputField = form.querySelector(`[name="${fieldName}[]"]`);
      const parentChecklist = inputField.closest('.meta-checklist');
      if (parentChecklist) {
        parentChecklist.classList.add('error-array-checklist');
      }
    }

    if (!inputField) {
      console.warn(`Input field "${fieldName}" not found in form.`);
      return;
    }

    const parentElement = inputField.parentElement;
    const isCheckboxOrRadio =
      inputField.type === 'checkbox' || inputField.type === 'radio';
    const isFile = inputField.type === 'file';
    const isSelect = inputField.tagName.toLowerCase() === 'select';

    const errorMessage = fieldError?.[0] || options.defaultErrorMessage;

    const errorTextClass = this.getErrorTextClass(
      isFile,
      isCheckboxOrRadio,
      isSelect,
      options
    );

    let errorTextElement = this.getErrorTextElement(
      isSelect,
      isFile,
      inputField,
      parentElement
    );

    if (!isCheckboxOrRadio && !isFile && !isSelect) {
      this.handleRegularFieldError(
        inputField,
        parentElement,
        errorTextElement,
        errorMessage,
        errorTextClass,
        options,
        errors,
        fieldName
      );
    } else if (isCheckboxOrRadio || isFile || isSelect) {
      this.handleSpecialFieldError(
        inputField,
        parentElement,
        errorTextElement,
        errorMessage,
        errorTextClass,
        options,
        errors,
        fieldName,
        isCheckboxOrRadio,
        isFile,
        isSelect
      );
    }

    this.handleMetaUploadFile(inputField);
    this.addFieldEventListeners(
      inputField,
      parentElement,
      errorTextElement,
      options
    );
  }

  /**
   * Get appropriate CSS class for error text based on field type
   * @param {boolean} isFile - Whether the field is a file input
   * @param {boolean} isCheckboxOrRadio - Whether the field is checkbox or radio
   * @param {boolean} isSelect - Whether the field is a select
   * @param {object} options - Validation options
   * @returns {string} CSS class for error text
   */
  getErrorTextClass(isFile, isCheckboxOrRadio, isSelect, options) {
    if (isFile) {
      return options.errorTextClassesFile;
    } else if (isCheckboxOrRadio) {
      return options.errorTextClassesCheckRadio;
    } else if (isSelect) {
      return options.errorTextClassesSelect;
    } else {
      return options.errorTextClasses;
    }
  }

  /**
   * Find existing error text element based on field type
   * @param {boolean} isSelect - Whether the field is a select
   * @param {boolean} isFile - Whether the field is a file input
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @returns {HTMLElement|null} Existing error text element or null
   */
  getErrorTextElement(isSelect, isFile, inputField, parentElement) {
    if (isFile) {
      return (
        this.getFileUploadWrapper(inputField)?.querySelector('.error-text') ??
        null
      );
    }

    return isSelect
      ? parentElement.parentElement.parentElement.querySelector('.error-text')
      : parentElement.querySelector('.error-text');
  }

  /**
   * Find the shared wrapper for a file upload input.
   * @param {HTMLElement} inputField - The file input element
   * @returns {HTMLElement|null} Upload wrapper or null
   */
  getFileUploadWrapper(inputField) {
    return inputField.closest('.meta-upload-file');
  }

  /**
   * Handle regular input field error display
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {string} errorMessage - Error message to display
   * @param {string} errorTextClass - CSS class for error text
   * @param {object} options - Validation options
   * @param {object} errors - Errors object
   * @param {string} fieldName - Name of the field
   */
  handleRegularFieldError(
    inputField,
    parentElement,
    errorTextElement,
    errorMessage,
    errorTextClass,
    options,
    errors,
    fieldName
  ) {
    errors[fieldName] = errorMessage;

    inputField.classList.add('error', ...options.inputErrorClasses.split(' '));

    if (!errorTextElement) {
      errorTextElement = document.createElement('span');
      errorTextElement.className = `error-text ${errorTextClass}`;
      parentElement.appendChild(errorTextElement);
    }
    errorTextElement.textContent = errorMessage;

    this.addErrorTextEventListener(
      errorTextElement,
      inputField,
      parentElement,
      options
    );
  }

  /**
   * Handle special field error display (checkbox, radio, file, select)
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {string} errorMessage - Error message to display
   * @param {string} errorTextClass - CSS class for error text
   * @param {object} options - Validation options
   * @param {object} errors - Errors object
   * @param {string} fieldName - Name of the field
   * @param {boolean} isCheckboxOrRadio - Whether the field is checkbox or radio
   * @param {boolean} isFile - Whether the field is a file input
   * @param {boolean} isSelect - Whether the field is a select
   */
  handleSpecialFieldError(
    inputField,
    parentElement,
    errorTextElement,
    errorMessage,
    errorTextClass,
    options,
    errors,
    fieldName,
    isCheckboxOrRadio,
    isFile,
    isSelect
  ) {
    errors[fieldName] = errorMessage;

    inputField.classList.add('error');

    if (isCheckboxOrRadio || isFile) {
      if (parentElement.closest('.error-array-checklist')) {
        const checkListArray = parentElement.closest('.error-array-checklist');
        checkListArray.classList.add('error');
      } else {
        parentElement.classList.add('error');
      }
    } else if (isSelect) {
      const selectParent = parentElement.parentElement;
      selectParent.classList.add(
        'error',
        ...options.selectErrorClasses.split(' ')
      );
    }

    if (!errorTextElement) {
      errorTextElement = document.createElement('span');
      errorTextElement.className = `error-text ${errorTextClass}`;

      if (isFile) {
        const errorContainer =
          this.getFileUploadWrapper(inputField) ?? parentElement;
        const errorTextsArr = errorContainer.querySelectorAll('.error-text');
        if (errorTextsArr.length) {
          errorTextsArr.forEach((el) => {
            el.remove();
          });
        }
        parentElement.insertAdjacentElement('afterend', errorTextElement);
      } else if (isSelect) {
        const selectParent = parentElement.parentElement;
        selectParent.insertAdjacentElement('afterend', errorTextElement);
      } else {
        parentElement.appendChild(errorTextElement);
      }
    }
    errorTextElement.textContent = errorMessage;

    if (parentElement.closest('.error-array-checklist')) {
      const checkListArray = inputField.closest('.error-array-checklist');

      const errorTextsArr = checkListArray.querySelectorAll('.error-text');
      if (errorTextsArr.length) {
        for (let i = 0; i < errorTextsArr.length; i++) {
          errorTextsArr[i].remove();
        }
      }

      checkListArray.appendChild(errorTextElement);
      errorTextElement.classList.remove(
        ...options.errorTextClassesCheckRadio.split(' ')
      );
      errorTextElement.classList.add(
        'w-full',
        ...options.errorTextClasses.split(' ')
      );

      this.addErrorChecklistArrayEventListener(
        errorTextElement,
        inputField,
        parentElement,
        options,
        fieldName
      );
    }

    this.addErrorTextEventListener(
      errorTextElement,
      inputField,
      parentElement,
      options
    );
  }

  /**
   * Handle meta upload file error styling
   * @param {HTMLElement} inputField - The input field element
   */
  handleMetaUploadFile(inputField) {
    const metaUploadFileEl = this.getFileUploadWrapper(inputField);
    if (metaUploadFileEl) {
      metaUploadFileEl.classList.add('error');
    }
  }

  addErrorChecklistArrayEventListener(
    errorTextElement,
    inputField,
    parentElement,
    options,
    fieldName
  ) {
    const checkListArray = inputField.closest('.error-array-checklist');

    if (checkListArray) {
      const checkListArrayElements = checkListArray.querySelectorAll(
        `[name="${fieldName}[]"]`
      );

      const errorTextHandler = () => {
        setTimeout(() => {
          this.clearFieldError(
            inputField,
            checkListArray,
            errorTextElement,
            options
          );

          setTimeout(() => {
            const errorTextsArr =
              checkListArray.querySelectorAll('.error-text');
            if (errorTextsArr.length) {
              for (let i = 0; i < errorTextsArr.length; i++) {
                errorTextsArr[i].remove();
              }
            }

            errorTextElement.removeEventListener('click', errorTextHandler);
            checkListArrayElements.forEach((el) => {
              el.removeEventListener('change', errorTextHandler);
            });
          }, options.errorClearDelay);
        }, options.errorClearDelay);
      };

      errorTextElement.addEventListener('click', errorTextHandler);

      checkListArrayElements.forEach((el) => {
        el.addEventListener('change', () => {
          errorTextHandler();
        });
      });
    }
  }

  /**
   * Add event listeners to error text element for error clearing
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {object} options - Validation options
   */
  addErrorTextEventListener(
    errorTextElement,
    inputField,
    parentElement,
    options
  ) {
    const oldErrorTextHandler = errorTextElement._validationErrorTextHandler;
    if (oldErrorTextHandler) {
      errorTextElement.removeEventListener('click', oldErrorTextHandler);
    }

    const errorTextHandler = () => {
      setTimeout(() => {
        this.clearFieldError(
          inputField,
          parentElement,
          errorTextElement,
          options
        );
      }, options.errorClearDelay);
    };

    errorTextElement.addEventListener('click', errorTextHandler);
    errorTextElement._validationErrorTextHandler = errorTextHandler;
  }

  /**
   * Add event listeners to field and parent elements for error clearing
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {object} options - Validation options
   */
  addFieldEventListeners(inputField, parentElement, errorTextElement, options) {
    const oldClickHandler = inputField._validationClickHandler;
    if (oldClickHandler) {
      inputField.removeEventListener('click', oldClickHandler);
    }

    const clickHandler = () => {
      setTimeout(() => {
        this.clearFieldError(
          inputField,
          parentElement,
          errorTextElement,
          options
        );
      }, options.errorClearDelay);
    };

    inputField.addEventListener('click', clickHandler);
    inputField._validationClickHandler = clickHandler;

    if (parentElement.parentElement) {
      const oldParentClickHandler =
        parentElement.parentElement._validationParentClickHandler;
      if (oldParentClickHandler) {
        parentElement.parentElement.removeEventListener(
          'click',
          oldParentClickHandler
        );
      }

      /*const parentClickHandler = () => {
        setTimeout(() => {
          this.clearParentError(inputField, parentElement, options);
        }, options.errorClearDelay);
      };

      parentElement.parentElement.addEventListener('click', parentClickHandler);
      parentElement.parentElement._validationParentClickHandler =
        parentClickHandler;*/
    }

    const oldDirectParentClickHandler =
      parentElement._validationDirectParentClickHandler;
    if (oldDirectParentClickHandler) {
      parentElement.removeEventListener('click', oldDirectParentClickHandler);
    }

    const directParentClickHandler = () => {
      setTimeout(() => {
        this.clearParentError(inputField, parentElement, options);
      }, options.errorClearDelay);
    };

    parentElement.addEventListener('click', directParentClickHandler);
    parentElement._validationDirectParentClickHandler =
      directParentClickHandler;
  }

  /**
   * Clear field error styling and remove error elements
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {HTMLElement} errorTextElement - Error text element
   * @param {object} options - Validation options
   */
  clearFieldError(inputField, parentElement, errorTextElement, options) {
    inputField.classList.remove(
      'error',
      ...options.inputErrorClasses.split(' ')
    );
    inputField.closest('form').classList.remove('error');

    const metaUploadFileEl = this.getFileUploadWrapper(inputField);
    if (metaUploadFileEl) {
      metaUploadFileEl.classList.remove('error');
    }

    const errorTextElements = parentElement.querySelectorAll('.error-text');
    errorTextElements.forEach((el) => {
      if (el.parentElement) {
        el.parentElement.classList.remove('error');
      }
      el.remove();
    });

    if (inputField.tagName.toLowerCase() === 'select') {
      const selectParent = parentElement.parentElement;
      if (selectParent) {
        const selectErrorElements =
          selectParent.querySelectorAll('.error-text');
        selectErrorElements.forEach((el) => {
          if (el.parentElement) {
            el.parentElement.classList.remove('error');
          }
          el.remove();
        });
        selectParent.classList.remove(
          'error',
          ...options.selectErrorClasses.split(' ')
        );
      }
    }

    if (inputField.type === 'file') {
      const nextSibling = parentElement.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('error-text')) {
        nextSibling.remove();
      }
    }
  }

  /**
   * Clear parent error styling and remove error elements
   * @param {HTMLElement} inputField - The input field element
   * @param {HTMLElement} parentElement - Parent element of the field
   * @param {object} options - Validation options
   */
  clearParentError(inputField, parentElement, options) {
    parentElement.classList.remove('error');
    inputField.closest('form').classList.remove('error');

    const metaUploadFileEl = this.getFileUploadWrapper(inputField);
    if (metaUploadFileEl) {
      metaUploadFileEl.classList.remove('error');
      const errorTextsArr = metaUploadFileEl.querySelectorAll('.error-text');
      if (errorTextsArr.length) {
        errorTextsArr.forEach((el) => {
          el.remove();
        });
      }
    }

    const errorTextElements = parentElement.querySelectorAll('.error-text');
    errorTextElements.forEach((el) => {
      if (el.parentElement) {
        el.parentElement.classList.remove('error');
      }
      el.remove();
    });

    if (inputField.tagName.toLowerCase() === 'select') {
      const selectParent = parentElement.parentElement;
      if (selectParent) {
        const selectErrorElements =
          selectParent.querySelectorAll('.error-text');
        selectErrorElements.forEach((el) => {
          if (el.parentElement) {
            el.parentElement.classList.remove('error');
          }
          el.remove();
        });
        selectParent.classList.remove(
          'error',
          ...options.selectErrorClasses.split(' ')
        );
      }
    }

    if (inputField.type === 'file') {
      const nextSibling = parentElement.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('error-text')) {
        nextSibling.remove();
      }
    }
  }

  /**
   * Update all SimpleBar instances to recalculate their dimensions
   */
  updateAllSimpleBars() {
    document.querySelectorAll('.simplebar-content').forEach((content) => {
      const simpleBarInstance = window.SimpleBar?.instances.get(
        content.closest('[data-simplebar]')
      );
      if (simpleBarInstance) {
        simpleBarInstance.recalculate();
      }
    });
  }
}

/**
 * Global form validation instance
 */
const formValidator = new FormValidator();

/**
 * Global form validation function for backward compatibility
 * @param {string|object} formData - Form data as string or object
 * @returns {object} Object containing field errors
 */
window.formValidation = function (formData) {
  return formValidator.validate(formData);
};
